import React from 'react';
import { TouchTarget } from 'apps/sigma2-ui/src/components/ui/TouchTarget';
import cn from 'classnames';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { TranslatedContent } from 'libs/end-to-end/src/translation-content';

export interface PrimaryFilterButtonProps {
  /**
   * Callback when the button is clicked
   */
  onClick: () => void;

  /**
   * Whether the button is disabled
   */
  disabled: boolean;

  /**
   * Whether the button is active (this turns it blue similiar to hover, but only if disabled is false)
   */
  active: boolean;

  /**
   * Whether the button has a selection, which bolds the right side of the button
   */
  hasSelection: boolean;

  /**
   * Translated content for the left side of the button
   */
  leftTranslatedContent: TranslatedContent;

  /**
   * Translated content for the right side of the button
   */
  rightTranslatedContent: TranslatedContent;

  icon?: React.ReactNode;
}

export const PrimaryFilterButton = ({
  onClick,
  disabled,
  active,
  hasSelection,
  leftTranslatedContent,
  rightTranslatedContent,
  icon,
}: PrimaryFilterButtonProps) => {
  return (
    <div className={'inline-block max-w-full'}>
      <TouchTarget
        disabled={disabled}
        content={({ hovered }) => {
          return (
            <span
              className={cn(
                'flex rounded py-2 px-3 border-[1px] gap-2 items-center transition-all',
                {
                  'opacity-50 cursor-not-allowed': disabled,
                  'opacity-100 cursor-pointer': !disabled,
                  'bg-white border-gray-200': !hovered && !active,
                  'bg-royal-100 border-royal-400': hovered || active,
                },
              )}
            >
              {icon && icon}
              <S2Typography
                translatedContent={leftTranslatedContent}
                variant={'body-7'}
                textColorVariant={hovered || active ? 'royal-700' : 'gray-900'}
              />
              <span
                className={cn('w-[1px] h-[16px] transition-all', {
                  'bg-gray-200': !hovered && !active,
                  'bg-royal-700': hovered || active,
                })}
              />
              <S2Typography
                translatedContent={rightTranslatedContent}
                variant={hasSelection ? 'headline-7-alt' : 'body-7'}
                textColorVariant={
                  hovered || active
                    ? 'royal-700'
                    : hasSelection
                      ? 'gray-900'
                      : 'gray-600'
                }
                truncate={true}
                flex={true}
              />
            </span>
          );
        }}
        behavior={{ type: 'onClick', onClick }}
      ></TouchTarget>
    </div>
  );
};
